import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Clock, Calendar, ArrowLeft, Share2, Phone, ChevronRight, BookOpen, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { blogsAPI } from '../lib/api';
import { getWhatsAppLink } from '../lib/utils';
import { Seo, tryRedirect, assetUrl, absUrl } from '../lib/seo';
import { LogoLoader } from '../components/common/LogoLoader';

export const BlogPostPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await blogsAPI.getBySlug(slug);
        setBlog(response.data);
        
        // Fetch related blogs from same category
        const relatedRes = await blogsAPI.getAll({ category: response.data.category, limit: 4 });
        setRelatedBlogs(relatedRes.data.filter(b => b.slug !== slug).slice(0, 3));
      } catch (error) {
        if (error.response?.status === 404 && (await tryRedirect(`/blog/${slug}`, navigate))) return;
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug, navigate]);

  const getCategoryColor = (cat) => {
    const colors = {
      skin: 'bg-amber-100 text-amber-800',
      hair: 'bg-gray-800 text-white',
      weight: 'bg-emerald-100 text-emerald-800',
      aesthetic: 'bg-purple-100 text-purple-800',
      general: 'bg-blue-100 text-blue-800',
    };
    return colors[cat] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-alabaster">
        <LogoLoader label="Loading article..." className="min-h-[70vh]" />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-alabaster flex items-center justify-center">
        <Seo title="Article not found | DrPortals" override={{ robots_index: false }} />
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-gray-700 mb-4">Article not found</h2>
          <Link to="/blogs">
            <Button>Browse All Articles</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-alabaster" data-testid="blog-post-page">
      <Seo
        page={blog}
        title={`${blog.title} | DrPortals`}
        description={blog.excerpt}
        image={blog.featured_image}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": blog.title,
          "description": blog.excerpt,
          "image": blog.featured_image ? absUrl(blog.featured_image) : undefined,
          "author": { "@type": "Organization", "name": blog.author },
          "publisher": { "@type": "Organization", "name": "DrPortals", "logo": { "@type": "ImageObject", "url": absUrl('/brand/logo-dark.png') } },
          "datePublished": blog.published_at || blog.created_at,
          "dateModified": blog.updated_at,
          "mainEntityOfPage": absUrl(`/blog/${blog.slug}`),
          "articleSection": blog.category,
          "keywords": (blog.tags || []).join(', ')
        }}
      />
      {/* Hero Image */}
      <div className="relative h-[40vh] md:h-[50vh] bg-[#23265d]">
        <img
          src={assetUrl(blog.featured_image) || 'https://images.unsplash.com/photo-1655026392641-bf283a5f12d4?auto=format&fit=crop&w=1200&q=75'}
          alt={blog.featured_image_alt || blog.title}
          fetchpriority="high"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#23265d] via-[#23265d]/50 to-transparent" />
        
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-4xl mx-auto px-4 pb-8">
            <Link to="/blogs" className="flex w-fit items-center text-white/80 hover:text-white mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Articles
            </Link>
            <Badge className={`mb-4 ${getCategoryColor(blog.category)}`}>
              {blog.category}
            </Badge>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {blog.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {blog.author}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {blog.read_time} min read
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <time dateTime={blog.published_at || blog.created_at}>
                  {new Date(blog.published_at || blog.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </time>
              </span>
              {blog.updated_at && (
                <span className="text-sm text-white/60" data-testid="blog-updated-date">
                  Updated <time dateTime={blog.updated_at}>{new Date(blog.updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</time>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <article className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm p-6 md:p-10">
              {/* Excerpt */}
              <p className="text-lg text-gray-600 leading-relaxed mb-8 pb-8 border-b border-gray-100">
                {blog.excerpt}
              </p>

              {/* Content */}
              <div 
                className="prose prose-lg max-w-none cms-content
                  prose-headings:font-heading prose-headings:text-navy-900
                  prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
                  prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3
                  prose-p:text-gray-700 prose-p:leading-relaxed
                  prose-ul:my-4 prose-li:text-gray-700
                  prose-a:text-[#23265d] prose-a:font-medium
                "
                data-testid="blog-content"
                dangerouslySetInnerHTML={{ __html: blog.content }}
              />

              {/* Tags */}
              {blog.tags?.length > 0 && (
                <div className="mt-10 pt-6 border-t border-gray-100">
                  <h4 className="text-sm font-medium text-gray-500 mb-3">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {blog.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="bg-gray-100">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Share */}
              <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between">
                <span className="text-sm text-gray-500">Share this article</span>
                <Button variant="outline" size="sm" className="gap-2" data-testid="blog-share-btn" onClick={() => {
                  const url = window.location.href;
                  if (navigator.share) navigator.share({ title: blog.title, url }).catch(() => {});
                  else window.open(`https://wa.me/?text=${encodeURIComponent(`${blog.title} ${url}`)}`, '_blank', 'noopener,noreferrer');
                }}>
                  <Share2 className="w-4 h-4" />
                  Share
                </Button>
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="space-y-6">
            {/* CTA Card */}
            <div className="bg-[#23265d] rounded-2xl p-6 text-white">
              <h3 className="font-heading font-semibold text-lg mb-3">
                Need Expert Advice?
              </h3>
              <p className="text-gray-300 text-sm mb-4">
                Connect with verified dermatologists for personalized treatment recommendations.
              </p>
              <a href={getWhatsAppLink("Hi, I read your blog and have a question.")} target="_blank" rel="noopener noreferrer">
                <Button className="w-full bg-[#25D366] hover:bg-[#25D366]/90 text-white gap-2">
                  <Phone className="w-4 h-4" />
                  Chat with Us
                </Button>
              </a>
            </div>

            {/* Related Articles */}
            {relatedBlogs.length > 0 && (
              <div className="bg-white rounded-2xl p-6">
                <h3 className="font-heading font-semibold text-lg mb-4">Related Articles</h3>
                <div className="space-y-4">
                  {relatedBlogs.map((related) => (
                    <Link
                      key={related.id}
                      to={`/blog/${related.slug}`}
                      className="group flex gap-3"
                    >
                      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={assetUrl(related.featured_image) || 'https://images.unsplash.com/photo-1655026392641-bf283a5f12d4?auto=format&fit=crop&w=1200&q=75'}
                          loading="lazy"
                          alt={related.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-navy-900 line-clamp-2 group-hover:text-[#23265d]/80">
                          {related.title}
                        </h4>
                        <span className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          {related.read_time} min
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
                <Link to="/blogs" className="flex items-center justify-center gap-1 text-sm text-[#23265d] font-medium mt-4 hover:gap-2 transition-all">
                  View All Articles
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </aside>
        </div>
      </div>

    </div>
  );
};
